import React, { useState, useEffect } from "react";
import api from "../../api/api";

import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  Chip,
} from "@mui/material";

import { FaBook, FaMoneyBillWave } from "react-icons/fa";

const TeacherData = () => {
  const [subjects, setSubjects] = useState([]);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("access");

  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(
          "/api/users/teacher-subjects/",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setSubjects(response.data.subjects || []);
        setTeacherInfo(response.data.teacher_info || null);
      } catch (err) {
        console.error("Error fetching teacher information:", err);

        setError(
          "Failed to fetch teacher information."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherData();
  }, [token]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 250,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography
        color="error"
        align="center"
      >
        {error}
      </Typography>
    );
  }

  /*
   * ============================================================
   * DIVISIONS
   * ============================================================
   *
   * Backend returns:
   *
   * teacher_info: {
   *     divisions: ["Division A", "Division B"],
   *     division_data: [
   *         { id: 1, name: "Division A" },
   *         { id: 2, name: "Division B" }
   *     ]
   * }
   *
   * We prefer division_data because it contains IDs and names.
   * If division_data is unavailable, divisions is used.
   */

  const divisionData =
    teacherInfo?.division_data || [];

  const divisionNames =
    teacherInfo?.divisions || [];

  const assignedDivisions =
    divisionData.length > 0
      ? divisionData
      : divisionNames.map(
          (division, index) => ({
            id: index,
            name: division,
          })
        );

  return (
    <Box
      sx={{
        width: "100%",
        mx: "auto",
      }}
    >

      {/* ======================================================
          TEACHER INFORMATION
      ====================================================== */}

      {teacherInfo && (
        <Card
          elevation={5}
          sx={{
            borderRadius: "18px",
            mb: 3,
            overflow: "hidden",
            background:
              "linear-gradient(135deg,#ffffff,#eef7ff)",
          }}
        >
          <CardContent>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>

              {/* =========================
                  QUALIFICATION
              ========================= */}

              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Qualification
                  </Typography>

                  <Typography
                    fontWeight="700"
                    sx={{
                      mt: 0.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {teacherInfo.qualification || "-"}
                  </Typography>
                </Paper>
              </Grid>


              {/* =========================
                  ASSIGNED DIVISIONS
              ========================= */}

              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    Division
                  </Typography>

                  {assignedDivisions.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.8,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {assignedDivisions.map(
                        (division, index) => (
                          <Chip
                            key={
                              division.id ??
                              index
                            }
                            label={
                              division.name ??
                              division
                            }
                            size="small"
                            sx={{
                              fontWeight: 700,
                              color: "#034f84",
                              backgroundColor:
                                "#e3f2fd",
                              border:
                                "1px solid #90caf9",
                              borderRadius:
                                "8px",
                            }}
                          />
                        )
                      )}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      No Divisions Assigned
                    </Typography>
                  )}
                </Paper>
              </Grid>


              {/* =========================
                  GRADE CLASSES
              ========================= */}

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Grade Classes
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {teacherInfo.grade_class?.length ? (
                      teacherInfo.grade_class.map(
                        (grade, index) => (
                          <Chip
                            key={index}
                            label={grade}
                            color="primary"
                            size="small"
                          />
                        )
                      )
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        No Classes Assigned
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>


              {/* =========================
                  DEPENDENT STUDENT
              ========================= */}

              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Dependent Student
                  </Typography>

                  <Typography
                    fontWeight="700"
                    sx={{ mt: 0.5 }}
                  >
                    {teacherInfo.dependent_student ||
                      "-"}
                  </Typography>
                </Paper>
              </Grid>


              {/* =========================
                  SALARY
              ========================= */}

              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Salary
                  </Typography>

                  <Typography
                    fontWeight="700"
                    color="success.main"
                    sx={{
                      mt: 0.5,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FaMoneyBillWave
                      style={{
                        marginRight: 6,
                      }}
                    />

                    $
                    {Number(
                      teacherInfo.salary || 0
                    ).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>

            </Grid>

          </CardContent>
        </Card>
      )}


      {/* ======================================================
          SUBJECTS
      ====================================================== */}

      <Card
        elevation={5}
        sx={{
          borderRadius: "18px",
          overflow: "hidden",
          background:
            "linear-gradient(135deg,#ffffff,#f8fbff)",
        }}
      >
        <CardContent>

          <Typography
            variant="h6"
            fontWeight="700"
            align="center"
            sx={{
              color: "#034f84",
              mb: 3,
            }}
          >
            My Assigned Subjects
          </Typography>

          {subjects.length === 0 ? (
            <Typography
              align="center"
              color="text.secondary"
            >
              No subjects assigned.
            </Typography>
          ) : (
            <Box
              sx={{
                maxHeight: 470,
                overflowY: "auto",
                pr: 1,

                "&::-webkit-scrollbar": {
                  width: 6,
                },

                "&::-webkit-scrollbar-thumb": {
                  background: "#bdbdbd",
                  borderRadius: 10,
                },
              }}
            >
              <Grid container spacing={2}>

                {subjects.map((subject) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={12}
                    key={subject.id}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        transition: ".3s",
                        cursor: "pointer",

                        "&:hover": {
                          transform:
                            "translateY(-3px)",
                          boxShadow: 6,
                        },
                      }}
                    >

                      {/* SUBJECT ICON */}

                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          minWidth: 52,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(135deg,#1976d2,#42a5f5)",
                          color: "#fff",
                        }}
                      >
                        <FaBook size={22} />
                      </Box>


                      {/* SUBJECT NAME */}

                      <Box flex={1}>
                        <Typography
                          fontWeight="700"
                          sx={{
                            wordBreak:
                              "break-word",
                          }}
                        >
                          {subject.name}
                        </Typography>
                      </Box>

                    </Paper>
                  </Grid>
                ))}

              </Grid>
            </Box>
          )}

        </CardContent>
      </Card>

    </Box>
  );
};

export default TeacherData;
